// Moved from the W0 spike (Web/spikes/automerge-interop/src); ports SuiteModel/AutomergeSupport.swift.
// Typed reads over the low-level Automerge API, with the Swift accessors' exact rules
// (SuiteModel/AutomergeSupport.swift): which scalar types each accessor accepts, text objects read as strings,
// null/absent → undefined, anything else → a wrong-type error naming the key. The proxy API can't do this:
// it reads int, uint and f64 alike as `number`, and scalar strings as `ImmutableString`.
import * as A from "@automerge/automerge";

export type ObjID = string;
export const ROOT: ObjID = "_root";

export class WrongType extends Error {
  constructor(public key: string, public datatype: string) {
    super(`Field '${key}' has an unexpected type (${datatype})`);
  }
}

type Full = [string, unknown] | null;

export class SwiftReader {
  readonly backend: ReturnType<typeof A.getBackend>;
  constructor(readonly doc: A.Doc<unknown>, readonly heads?: string[]) {
    this.backend = A.getBackend(doc);
  }

  /** At `heads` (a read-only view of the same document). */
  at(heads: string[]): SwiftReader {
    return new SwiftReader(this.doc, heads);
  }

  get(obj: ObjID, key: string | number): Full {
    return (this.backend.getWithType(obj, key, this.heads) as Full) ?? null;
  }

  keys(obj: ObjID): string[] {
    return this.backend.keys(obj, this.heads);
  }

  length(obj: ObjID): number {
    return this.backend.length(obj, this.heads);
  }

  text(obj: ObjID): string {
    return this.backend.text(obj, this.heads);
  }

  string(obj: ObjID, key: string): string | undefined {
    const v = this.get(obj, key);
    if (!v || v[0] === "null") return undefined;
    if (v[0] === "str") return v[1] as string;
    if (v[0] === "text") return this.text(v[1] as ObjID);
    throw new WrongType(key, v[0]);
  }

  requiredString(obj: ObjID, key: string): string {
    const s = this.string(obj, key);
    if (s === undefined) throw new Error(`Missing field '${key}'`);
    return s;
  }

  int(obj: ObjID, key: string): number | undefined {
    const v = this.get(obj, key);
    if (!v || v[0] === "null") return undefined;
    if (v[0] === "int" || v[0] === "uint" || v[0] === "counter") return Number(v[1]);
    throw new WrongType(key, v[0]);
  }

  uint(obj: ObjID, key: string): number | undefined {
    const i = this.int(obj, key);
    return i === undefined ? undefined : Math.max(0, i);
  }

  double(obj: ObjID, key: string): number | undefined {
    const v = this.get(obj, key);
    if (!v || v[0] === "null") return undefined;
    if (v[0] === "f64" || v[0] === "int" || v[0] === "uint") return Number(v[1]);
    throw new WrongType(key, v[0]);
  }

  bool(obj: ObjID, key: string): boolean | undefined {
    const v = this.get(obj, key);
    if (!v || v[0] === "null") return undefined;
    if (v[0] === "boolean") return v[1] as boolean;
    throw new WrongType(key, v[0]);
  }

  date(obj: ObjID, key: string): Date | undefined {
    const v = this.get(obj, key);
    if (!v || v[0] === "null") return undefined;
    if (v[0] === "timestamp") return v[1] as Date;
    throw new WrongType(key, v[0]);
  }

  map(obj: ObjID, key: string): ObjID | undefined {
    const v = this.get(obj, key);
    if (!v) return undefined;
    if (v[0] === "map") return v[1] as ObjID;
    throw new WrongType(key, v[0]);
  }

  list(obj: ObjID, key: string): ObjID | undefined {
    const v = this.get(obj, key);
    if (!v) return undefined;
    if (v[0] === "list") return v[1] as ObjID;
    throw new WrongType(key, v[0]);
  }

  textObject(obj: ObjID, key: string): ObjID | undefined {
    const v = this.get(obj, key);
    if (!v) return undefined;
    if (v[0] === "text") return v[1] as ObjID;
    throw new WrongType(key, v[0]);
  }

  /** `mapEntries`: key → [datatype, value or object id]. */
  entries(obj: ObjID): [string, [string, unknown]][] {
    return this.keys(obj).flatMap((k) => {
      const v = this.get(obj, k);
      return v ? [[k, v] as [string, [string, unknown]]] : [];
    });
  }

  /** List values as [datatype, value]. */
  values(obj: ObjID): [string, unknown][] {
    const out: [string, unknown][] = [];
    for (let i = 0; i < this.length(obj); i++) {
      const v = this.get(obj, i);
      if (v) out.push(v);
    }
    return out;
  }
}

/** Every value in a document with its Automerge datatype, as a plain tree (for comparing Swift- and JS-written shapes). */
export type TypedTree =
  | { t: "map"; v: Record<string, TypedTree> }
  | { t: "list"; v: TypedTree[] }
  | { t: "text"; v: string }
  | { t: string; v: unknown };

export function typedTree(r: SwiftReader, obj: ObjID = ROOT, kind: "map" | "list" | "text" = "map"): TypedTree {
  if (kind === "text") return { t: "text", v: r.text(obj) };
  const child = (full: [string, unknown]): TypedTree => {
    const [t, v] = full;
    if (t === "map" || t === "list" || t === "text") return typedTree(r, v as ObjID, t);
    return { t, v: v instanceof Date ? v.getTime() : typeof v === "bigint" ? Number(v) : v };
  };
  if (kind === "list") return { t: "list", v: r.values(obj).map(child) };
  const out: Record<string, TypedTree> = {};
  for (const [k, full] of r.entries(obj)) out[k] = child(full);
  return { t: "map", v: out };
}
